import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import { FlexRow, FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { ButtonChipItem } from "@similarweb/ui-components";
import { i18nFilter } from "app/filters/i18nFilter";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { getDefaultParamsForState } from "app/pages/digital-suite/home-page/helpers";
import type { IStepMetadata } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import { AutocompleteKeywords } from "pro-features/components/AutocompleteKeywords/AutocompleteKeywords";
import { Step } from "app/pages/digital-suite/home-page/components/ActivationWizard/Components/Step";
import { ButtonsList } from "app/pages/digital-suite/home-page/components/ActivationWizard/Components/ButtonsList";
import { setWizardCustomData } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardApi";

const TopicContainer = styled(FlexRow)`
    align-items: center;
    margin-top: 12px;
`;

const TopicsLabel = styled.p`
    margin: 0 8px 0;
    ${setFontVariant("subtitle2", colorsPalettes.carbon[250])};
`;

const exampleKeywords = ["chatgpt", "travel", "artificial intelligence", "crypto"];

export const GenerateTopicKeywordsStep: React.FC<IStepMetadata> = (props) => {
    const i18n = i18nFilter();

    const [fieldValue, setFieldValue] = useState<string>("");
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

    const popularTopics = [
        i18n("growth.homepage.activationWizard.step.generate_topic_keywords.topics.1"),
        i18n("growth.homepage.activationWizard.step.generate_topic_keywords.topics.2"),
        i18n("growth.homepage.activationWizard.step.generate_topic_keywords.topics.3"),
    ];

    const onSearch = (keyword?: string): void => {
        props.onStepComplete("growth.homepage.wizard.button.generateTopicKeywords");
        onGenerateKeyword(keyword);
    };

    const onGenerateKeyword = (keyword?: string): void => {
        if (keyword) {
            setWizardCustomData(props.role, "keyword", keyword);
        }

        proNavigator.go("digitalsuite_findkeywords_keywordGeneratorTool", {
            ...getDefaultParamsForState("digitalsuite_findkeywords_keywordGeneratorTool"),
            keyword: keyword || "face mask",
        });
    };

    const onExampleTopicClick = (topic: string): void => {
        setFieldValue(topic);
        props.trackWithGoal("growth.homepage.wizard.button.generateTopicKeywords.example", "click", {
            step: `${props.index + 1}`,
        });
    };

    return (
        <Step
            {...props}
            title={i18n("growth.homepage.activationWizard.step.generate_topic_keywords.title")}
            description={i18n("growth.homepage.activationWizard.step.generate_topic_keywords.description")}
            additions={
                <FlexColumn>
                    <AutocompleteKeywords
                        selectedValue={fieldValue}
                        onValueChange={(value: string) => setFieldValue(value)}
                        onClick={(selectedValue) => setFieldValue(selectedValue)}
                        emptyState={{
                            emptyStateKeywords: exampleKeywords.map((keyword) => ({
                                name: keyword,
                            })),
                            emptyStateTitle: i18n(
                                "growth.homepage.activationWizard.step.generate_topic_keywords.emptyStateTitle",
                            ),
                        }}
                        autocompleteProps={{
                            searchIcon: dropdownOpen && "search",
                            onFocus: () => setDropdownOpen(true),
                            onBlur: () => setDropdownOpen(false),
                            placeholder: i18n(
                                "growth.homepage.activationWizard.step.generate_topic_keywords.dropdown_placeholder",
                            ),
                        }}
                    />
                    <TopicContainer>
                        <TopicsLabel>
                            <I18n>growth.homepage.activationWizard.step.generate_topic_keywords.topics_label</I18n>
                        </TopicsLabel>
                        <FlexRow>
                            {popularTopics.map((topic) => (
                                <ButtonChipItem
                                    key={topic}
                                    text={topic}
                                    hideActionButton
                                    onClick={() => onExampleTopicClick(topic)}
                                />
                            ))}
                        </FlexRow>
                    </TopicContainer>
                </FlexColumn>
            }
            buttonsList={
                <ButtonsList
                    buttons={[
                        {
                            text: i18n("growth.homepage.activationWizard.step.generate_topic_keywords.button"),
                            onClick: () => onSearch(fieldValue),
                            type: "primary",
                            icon: "arrow-right",
                        },
                    ]}
                />
            }
        />
    );
};
